// packages/web/src/hooks/useAgentSkills.ts
// Hook to fetch AI agent skills from the agent marketplace API

import { useCallback, useEffect, useState } from 'react';

import type { AgentSkill } from '@/components/marketplace/AgentSkillCard';

const API_BASE = '/api/v1';

interface AgentSkillsResult {
  skills: AgentSkill[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useAgentSkills(): AgentSkillsResult {
  const [skills, setSkills] = useState<AgentSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);

  const fetchSkills = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${API_BASE}/catalog`);
      if (!res.ok) {
        throw new Error(`Failed to fetch agent skills: ${res.status}`);
      }

      const data = await res.json();
      const services: AgentSkill[] = (data.services || []).map((s: any) => ({
        ...s,
        agentName: s.agentName || undefined,
      }));

      setSkills(services);
    } catch (err: any) {
      console.error('[useAgentSkills] Error:', err);
      setError(err.message || 'Failed to load AI skills');
      setSkills([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-fetch on mount — uses fetched flag to ensure the effect isn't tree-shaken
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      fetchSkills();
    }
  }, [fetched, fetchSkills]);

  return { skills, loading, error, refetch: fetchSkills };
}
